import { useCallback, useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  exportSuperAdminCancellationsReport,
  getSuperAdminCancellationsFilters,
  postSuperAdminCancellationsReport,
} from "../api/superadminReports";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const TOOLTIP_MAX_WIDTH = 240;
const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];
const DEFAULT_BOOKING_SOURCES = [
  { value: "portal", label: "Client portal" },
  { value: "staff", label: "Staff / walk-in / phone" },
];
const FIELD_TOOLTIPS = {
  scope: "Run across all salons, one business, or one salon.",
  business_scope:
    "Optional for All Salons, required for Business scope. Also narrows Salon options.",
  salon_scope:
    "Required for Salon scope. You can also narrow results to one salon in broader scopes.",
  booking_source:
    "Portal includes client self-service bookings/changes. Staff includes front desk, walk-in, and phone updates.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "cancellations",
  title: "Cancellations & No-Shows (Super Admin)",
  subtitle:
    "Review cancellations, reschedules, and no-show activity across scope.",
  summaryLabel: "Cancellation Summary",
  detailsLabel: "Cancellation Details",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("cancellations");

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(value);
    } catch (err) {
      // Fall back to local time if timezone is invalid.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const eventSummaryText = (row) =>
  `${row.cancelled || 0} cancelled | ${row.rescheduled || 0} rescheduled | ${
    row.late_cancel_attempts || 0
  } late cancels | ${row.late_reschedule_attempts || 0} late reschedules`;

export default function SuperAdminCancellationsNoShows() {
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [showSalonBreakdown, setShowSalonBreakdown] = useState(false);

  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookingSourceOptions, setBookingSourceOptions] = useState(
    DEFAULT_BOOKING_SOURCES
  );
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [salons]);

  const broadScope = scope === "all" || scope === "business";

  useEffect(() => {
    if (!broadScope) {
      setShowSalonBreakdown(false);
    }
  }, [broadScope]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (selectedSalon?.timezone) return selectedSalon.timezone;
    }
    if (businessId) {
      const selectedBusiness = businessMap.get(String(businessId));
      if (selectedBusiness?.timezone) return selectedBusiness.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  const today = useMemo(() => formatDateISO(new Date(), anchorTimezone), [anchorTimezone]);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [technicianId, setTechnicianId] = useState("");
  const [serviceCategoryId, setServiceCategoryId] = useState("");
  const [bookingSource, setBookingSource] = useState("");

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setStartDate(today);
    setEndDate(today);
  }, [today]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (item) => String(item.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const loadFilters = useCallback(async () => {
    if (!is_super_admin) return;
    setFiltersLoading(true);
    setFiltersError("");
    try {
      const data = await getSuperAdminCancellationsFilters({
        scope,
        business_id: businessId || undefined,
        salon_id: salonId || undefined,
      });
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
      setTechnicians(Array.isArray(data?.technicians) ? data.technicians : []);
      setCategories(
        Array.isArray(data?.service_categories) ? data.service_categories : []
      );
      setBookingSourceOptions(
        Array.isArray(data?.booking_source_options) &&
          data.booking_source_options.length
          ? data.booking_source_options
          : DEFAULT_BOOKING_SOURCES
      );
    } catch (err) {
      setBusinesses([]);
      setSalons([]);
      setTechnicians([]);
      setCategories([]);
      setBookingSourceOptions(DEFAULT_BOOKING_SOURCES);
      setFiltersError(err?.message || "Unable to load scope filters.");
    } finally {
      setFiltersLoading(false);
    }
  }, [is_super_admin, scope, businessId, salonId]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (
      technicianId &&
      !(technicians || []).some((item) => String(item.id) === String(technicianId))
    ) {
      setTechnicianId("");
    }
  }, [technicianId, technicians]);

  useEffect(() => {
    if (
      serviceCategoryId &&
      !(categories || []).some((item) => String(item.id) === String(serviceCategoryId))
    ) {
      setServiceCategoryId("");
    }
  }, [serviceCategoryId, categories]);

  useEffect(() => {
    if (
      bookingSource &&
      !(bookingSourceOptions || []).some((item) => item.value === bookingSource)
    ) {
      setBookingSource("");
    }
  }, [bookingSource, bookingSourceOptions]);

  const handleScopeChange = (value) => {
    setScope(value);
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!salonId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (
      selectedSalon &&
      value &&
      String(selectedSalon.business_id || "") !== String(value)
    ) {
      setSalonId("");
    }
    if (!value && scope !== "salon") {
      setSalonId("");
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const isInvalidDateRange = useMemo(
    () => startDate && endDate && startDate > endDate,
    [startDate, endDate]
  );

  const validateScope = () => {
    if (scope === "business" && !businessId) {
      return "Choose a business for Business scope.";
    }
    if (scope === "salon" && !salonId) {
      return "Choose a salon for Salon scope.";
    }
    if (isInvalidDateRange) {
      return "End date cannot be before start date.";
    }
    return "";
  };

  const buildPayload = () => ({
    scope,
    business_id: businessId ? Number(businessId) : undefined,
    salon_id: salonId ? Number(salonId) : undefined,
    start_date: startDate,
    end_date: endDate,
    technician_id: technicianId ? Number(technicianId) : null,
    service_category_id: serviceCategoryId ? Number(serviceCategoryId) : null,
    booking_source: bookingSource || null,
    show_salon_breakdown: broadScope ? showSalonBreakdown : false,
  });

  const handleRunReport = async () => {
    const validationError = validateScope();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postSuperAdminCancellationsReport(buildPayload());
      setReportData(data || null);
    } catch (err) {
      setReportData(null);
      setError(err?.message || "Unable to load cancellation analytics.");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    const validationError = validateScope();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      await exportSuperAdminCancellationsReport(buildPayload());
    } catch (err) {
      setError(err?.message || "Unable to export data.");
    } finally {
      setLoading(false);
    }
  };

  const kpis = reportData?.kpis || {};
  const byDay = reportData?.by_day_of_week || [];
  const byTime = reportData?.by_time_of_day || [];
  const byTechnician = reportData?.by_technician || [];
  const topReasons = reportData?.top_reasons || [];
  const showBreakdownColumns = broadScope && showSalonBreakdown;

  const timezoneLine = useMemo(() => {
    if (scope === "salon") {
      const reportTimezone =
        reportData?.meta?.timezone && reportData?.meta?.timezone !== "per-salon"
          ? reportData.meta.timezone
          : null;
      const selectedSalonTimezone = salonMap.get(String(salonId))?.timezone;
      const timezoneLabel = reportTimezone || selectedSalonTimezone || anchorTimezone;
      return `Times shown in ${timezoneLabel}.`;
    }
    return "Times shown in each salon's local timezone (accounting).";
  }, [scope, reportData, salonMap, salonId, anchorTimezone]);

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className={`reportSectionShell min-h-screen bg-slate-100 ${MODAL_TEXT}`}>
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Cancellations & No-Shows"
            closeTo="/superadmin/reports"
          />
          <div className="px-6 py-6 space-y-6">
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Scope</label>
                    <InfoTooltip
                      text={FIELD_TOOLTIPS.scope}
                      label="Scope"
                      maxWidth={TOOLTIP_MAX_WIDTH}
                    />
                  </div>
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Business</label>
                    <InfoTooltip
                      text={FIELD_TOOLTIPS.business_scope}
                      label="Business"
                      maxWidth={TOOLTIP_MAX_WIDTH}
                    />
                  </div>
                  <select
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    <option value="">All Businesses</option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Salon</label>
                    <InfoTooltip
                      text={FIELD_TOOLTIPS.salon_scope}
                      label="Salon"
                      maxWidth={TOOLTIP_MAX_WIDTH}
                    />
                  </div>
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={
                      filtersLoading ||
                      !(scope === "salon" || scope === "business" || Boolean(businessId))
                    }
                  >
                    <option value="">
                      {scope === "salon" ? "Select Salon" : "All Salons"}
                    </option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name} (ID {salon.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {broadScope ? (
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={showSalonBreakdown}
                    onChange={(event) => setShowSalonBreakdown(event.target.checked)}
                  />
                  Show salon breakdown
                </label>
              ) : null}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Technician</label>
                  <select
                    value={technicianId}
                    onChange={(event) => setTechnicianId(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    <option value="">All technicians</option>
                    {(technicians || []).map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.display_name || person.name}
                        {broadScope && person.salon_name ? ` (${person.salon_name})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={LABEL_TEXT}>Service category</label>
                  <select
                    value={serviceCategoryId}
                    onChange={(event) => setServiceCategoryId(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    <option value="">All categories</option>
                    {(categories || []).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                        {broadScope && category.salon_name
                          ? ` (${category.salon_name})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Booking source</label>
                    <InfoTooltip
                      text={FIELD_TOOLTIPS.booking_source}
                      label="Booking source"
                      maxWidth={TOOLTIP_MAX_WIDTH}
                    />
                  </div>
                  <select
                    value={bookingSource}
                    onChange={(event) => setBookingSource(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    <option value="">All sources</option>
                    {(bookingSourceOptions || DEFAULT_BOOKING_SOURCES).map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                id={HELP_ANCHORS.export}
                className={`flex flex-wrap gap-3 rounded-2xl ${getSectionClassName(
                  HELP_ANCHORS.export
                )}`}
              >
                <button
                  type="button"
                  onClick={handleRunReport}
                  disabled={loading}
                  className={BTN_PRIMARY}
                >
                  {loading ? "Loading..." : "Run report"}
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={loading}
                  className={BTN_OUTLINE}
                >
                  Export data file
                </button>
                <div className="text-sm text-slate-500">{timezoneLine}</div>
              </div>

              {filtersError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700">
                  {filtersError}
                </div>
              ) : null}

              {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                  {error}
                </div>
              ) : null}
            </div>

            {reportData ? (
              <div className="space-y-6">
                <div
                  id={HELP_ANCHORS.kpis}
                  className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6 ${getSectionClassName(
                    HELP_ANCHORS.kpis
                  )}`}
                >
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Total appointments
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.total_appointments || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Cancelled
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.cancelled || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Rescheduled
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.rescheduled || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Late cancel attempts
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.late_cancel_attempts || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Late reschedule attempts
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.late_reschedule_attempts || 0}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-xs text-slate-500 uppercase font-semibold">
                      Cancellation rate
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {kpis.cancellation_rate || 0}%
                    </div>
                  </div>
                </div>

                <div
                  id={HELP_ANCHORS.details}
                  className={`grid grid-cols-1 gap-6 lg:grid-cols-2 ${getSectionClassName(
                    HELP_ANCHORS.details
                  )}`}
                >
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      By day of week
                    </h3>
                    <div className="space-y-2 text-sm text-slate-700">
                      {byDay.map((row) => (
                        <div
                          key={row.label}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                        >
                          <span>{row.label}</span>
                          <span className="text-slate-500">{eventSummaryText(row)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      By time of day
                    </h3>
                    <div className="space-y-2 text-sm text-slate-700">
                      {byTime.map((row) => (
                        <div
                          key={row.label}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                        >
                          <span>{row.label}</span>
                          <span className="text-slate-500">{eventSummaryText(row)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      By technician
                    </h3>
                    {showBreakdownColumns ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold">Business</th>
                              <th className="px-4 py-3 text-left font-semibold">Salon</th>
                              <th className="px-4 py-3 text-left font-semibold">Technician</th>
                              <th className="px-4 py-3 text-left font-semibold">Summary</th>
                            </tr>
                          </thead>
                          <tbody>
                            {byTechnician.map((row) => (
                              <tr
                                key={`${row.staff_id || "u"}-${row.salon_id || "s"}-${row.staff_name}`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-4 py-3 text-slate-700">
                                  {row.business_name || "--"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.salon_name || "--"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.staff_name || "Unassigned"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {eventSummaryText(row)}
                                </td>
                              </tr>
                            ))}
                            {!byTechnician.length ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-4 py-6 text-center text-slate-500"
                                >
                                  No technician data.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm text-slate-700">
                        {byTechnician.map((row) => (
                          <div
                            key={row.staff_id || row.staff_name}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                          >
                            <span>{row.staff_name}</span>
                            <span className="text-slate-500">{eventSummaryText(row)}</span>
                          </div>
                        ))}
                        {!byTechnician.length ? (
                          <div className="text-slate-500">No technician data.</div>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      Top reasons
                    </h3>
                    {showBreakdownColumns ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold">Reason</th>
                              <th className="px-4 py-3 text-left font-semibold">Count</th>
                              <th className="px-4 py-3 text-left font-semibold">Business</th>
                              <th className="px-4 py-3 text-left font-semibold">Salon</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topReasons.map((row, index) => (
                              <tr
                                key={`${row.reason || "reason"}-${row.salon_id || "salon"}-${index}`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-4 py-3 text-slate-700">
                                  {row.reason || "Not shared"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.count || 0}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.business_name || "--"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.salon_name || "--"}
                                </td>
                              </tr>
                            ))}
                            {!topReasons.length ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-4 py-6 text-center text-slate-500"
                                >
                                  No reasons recorded.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm text-slate-700">
                        {topReasons.length === 0 ? (
                          <div className="text-slate-500">No reasons recorded.</div>
                        ) : null}
                        {topReasons.map((row, index) => (
                          <div
                            key={`${row.reason || "reason"}-${index}`}
                            className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2"
                          >
                            <span>{row.reason}</span>
                            <span className="text-slate-500">{row.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

          </div>
        </div>
      </div>
    </div>
  );
}
